<template>
  <LandingBackground>
    <PublicLandingHeader
      :title="appSettingsStore.applicationTitle"
      subtitle="Hawaii Environmental Health Administration Hazard Evaluation and Emergency Response Office"
    />

    <PublicLandingContainer class="grid justify-start gap-16 xl:grid-cols-2 2xl:grid-cols-3">
      <PublicLandingBackdrop class="grid grid-cols-1 gap-10 2xl:col-span-2 2xl:grid-cols-2">
        <PublicLandingOnlineServices
          class="2xl:col-span-2"
          title="Employee Services"
          body="The iHEER System provides online employee to emergency response incidents, HEPCRA facilities, discovery and remediation sites, and hazard evaluation inquiries."
          hide-register
        ></PublicLandingOnlineServices>

        <PublicLandingCard>
          <PublicLandingCardSection label="Emergency Response Incidents">
            <PublicLandingParagraph
              >Find, view, and manage hazardous substance spills and incident responses</PublicLandingParagraph
            >
            <PublicLandingList>
              <PublicLandingListItem label="Log reported incidents" />
              <PublicLandingListItem label="Manage location and substances" />
              <PublicLandingListItem label="Monitor case until no further action" />
            </PublicLandingList>
          </PublicLandingCardSection>

          <PublicLandingCardSection label="HEPCRA Facilities">
            <PublicLandingParagraph
              >Find, view, and manage facilities required to report hazardous and toxic
              chemicals</PublicLandingParagraph
            >
            <PublicLandingList>
              <PublicLandingListItem label="Manage facility locations" />
              <PublicLandingListItem label="Monitor facility status" />
            </PublicLandingList>
          </PublicLandingCardSection>
        </PublicLandingCard>

        <PublicLandingCard>
          <PublicLandingCardSection label="Discovery and Remediation Sites">
            <PublicLandingParagraph
              >Find, view, and manage site identification, investigation, and remediation
              activities</PublicLandingParagraph
            >
            <PublicLandingList>
              <PublicLandingListItem label="Manage site locations" />
              <PublicLandingListItem label="Monitor site status and priority" />
              <PublicLandingListItem label="Track site owners and operators" />
            </PublicLandingList>
          </PublicLandingCardSection>

          <PublicLandingCardSection label="Hazard Evaluation Inquiries">
            <PublicLandingParagraph
              >Find, view, and manage reported pesticide and heavy metal poisoning</PublicLandingParagraph
            >
            <PublicLandingList>
              <PublicLandingListItem label="Log environmental health inquiries" />
              <PublicLandingListItem label="Monitor inquiry status" />
            </PublicLandingList>
          </PublicLandingCardSection>
        </PublicLandingCard>
      </PublicLandingBackdrop>

      <PublicLandingBackdrop class="grid gap-10 self-start">
        <PublicLandingCard>
          <PublicLandingCardSection label="Public Records">
            <PublicLandingParagraph> Emergency Response Incidents </PublicLandingParagraph>
            <PublicLandingList class="mb-3">
              <PublicLandingListItem
                ><router-link :to="{ name: routeNames.incidents.incidents }" class="text-sky-600 hover:underline"
                  >Search hazardous substance spills and incident responses</router-link
                ></PublicLandingListItem
              >
            </PublicLandingList>
            <PublicLandingParagraph> HEPCRA Facilities </PublicLandingParagraph>
            <PublicLandingList class="mb-3">
              <PublicLandingListItem
                ><router-link :to="{ name: routeNames.facilities.facilities }" class="text-sky-600 hover:underline"
                  >Search facilities required to report hazardous and toxic chemicals</router-link
                ></PublicLandingListItem
              >
            </PublicLandingList>
            <PublicLandingParagraph> Discovery and Remediation Sites </PublicLandingParagraph>
            <PublicLandingList class="mb-3">
              <PublicLandingListItem
                ><router-link :to="{ name: routeNames.sites.sites }" class="text-sky-600 hover:underline"
                  >Search contaminated site identification, investigation, and remediation activities</router-link
                ></PublicLandingListItem
              >
            </PublicLandingList>
          </PublicLandingCardSection>
        </PublicLandingCard>
      </PublicLandingBackdrop>
    </PublicLandingContainer>

    <MdrnFooter light black-and-white />
  </LandingBackground>
</template>

<script lang="ts">
import LandingBackground from "@/components/LandingBackground.vue";
import { InitialBadge } from "@modernary/mui/components/badge";
import { BaseButton } from "@modernary/mui/components/button";
import { MdrnFooter, NoneFoundFlag, useAppNav, useAppSettingsStore } from "@modernary/mui/components/common";
import { LoginButton, LogoutButton, RegisterButton } from "@modernary/mui/utils/authentication/buttons";
import { computed, defineComponent } from "vue";

import { routeNames } from "@/routes";
import {
  PublicLandingBackdrop,
  PublicLandingCard,
  PublicLandingCardSection,
  PublicLandingContainer,
  PublicLandingHeader,
  PublicLandingLinkCard,
  PublicLandingList,
  PublicLandingListItem,
  PublicLandingOnlineServices,
  PublicLandingParagraph,
} from "@modernary/mui/components/publicLanding";

// Home content for unauth'd users
export default defineComponent({
  name: "HomePublic",
  components: {
    BaseButton,
    InitialBadge,
    LoginButton,
    RegisterButton,
    MdrnFooter,
    NoneFoundFlag,
    LogoutButton,
    PublicLandingCard,
    PublicLandingCardSection,
    PublicLandingList,
    PublicLandingListItem,
    PublicLandingParagraph,
    PublicLandingLinkCard,
    PublicLandingOnlineServices,
    PublicLandingHeader,
    PublicLandingContainer,
    PublicLandingBackdrop,
    LandingBackground,
  },
  setup() {
    const appSettingsStore = useAppSettingsStore();

    return { BaseButton, appSettingsStore, routeNames };
  },
});
</script>

<style lang="css" module></style>
